/**
 * Authentication Context for DefencIQ
 * Manages user authentication state and API calls
 *
 * Uses relative URLs (/api/...) so the Kubernetes ingress handles routing
 * to the backend in both preview and deployed environments, avoiding
 * any issues with the platform's fetch interceptor.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  }, []);

  // API helper with auth — uses relative URLs
  const authFetch = useCallback(async (endpoint, options = {}) => {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(endpoint, { ...options, headers });

    if (response.status === 401) {
      logout();
      throw new Error('Session expired. Please login again.');
    }

    return response;
  }, [token, logout]);

  // Check if user is logged in on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (!token) { setLoading(false); return; }

      try {
        const response = await authFetch('/api/auth/me');
        if (response.ok) {
          setUser(await response.json());
        } else {
          localStorage.removeItem('token');
          setToken(null);
        }
      } catch {
        localStorage.removeItem('token');
        setToken(null);
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, [token, authFetch]);

  const login = async (email, password) => {
    setError(null);
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      let data;
      try {
        data = JSON.parse(await response.text());
      } catch {
        if (!response.ok) {
          const msgs = { 401: 'Invalid email or password.', 403: 'Account is disabled. Contact an administrator.' };
          throw new Error(msgs[response.status] || `Login failed (${response.status})`);
        }
        throw new Error('Login failed');
      }

      if (!response.ok) throw new Error(data.detail || 'Login failed');

      localStorage.setItem('token', data.access_token);
      setToken(data.access_token);
      setUser(data.user);
      return data.user;
    } catch (err) {
      const message = err.message || 'Login failed';
      setError(message);
      throw new Error(message);
    }
  };

  const register = async (email, name, password) => {
    setError(null);
    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name, password }),
      });

      let data;
      try {
        data = JSON.parse(await response.text());
      } catch {
        if (!response.ok) {
          const msgs = { 403: 'Registration is disabled. Contact an administrator.', 400: 'Invalid registration data.', 409: 'Email already registered.' };
          throw new Error(msgs[response.status] || `Registration failed (${response.status})`);
        }
        throw new Error('Registration failed');
      }

      if (!response.ok) throw new Error(data.detail || `Registration failed (${response.status})`);

      localStorage.setItem('token', data.access_token);
      setToken(data.access_token);
      setUser(data.user);
      return data.user;
    } catch (err) {
      const message = err.message || 'Registration failed';
      setError(message);
      throw new Error(message);
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    const response = await authFetch('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    });

    if (!response.ok) {
      let detail = 'Password change failed';
      try { detail = JSON.parse(await response.text()).detail || detail; } catch {}
      throw new Error(detail);
    }
    return true;
  };

  const value = {
    user, token, loading, error,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    login, register, logout, changePassword, authFetch,
    clearError: () => setError(null),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
